import React from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import commonStyles from "./../../../finecoPortfolio.common.module.scss";
import { utilsFormatDatePortfolioEvolutionChart } from "utils/dateUtils";
import colorsStore from "shared/store/colorsStore";
import { useTranslation } from "react-i18next";
import { formatItalianNumber } from "utils/formatUtils";
import { useComparisonLabels } from "../../../compare/ComparisonLabelsContext";

type PortfolioEvolutionChartProps = {
  data: any;
  data2: any;
};

const PortfolioEvolutionChart: React.FC<PortfolioEvolutionChartProps> = ({
  data,
  data2,
}) => {
  useTranslation();
  const labels = useComparisonLabels();
  const colorsConfig = colorsStore((state) => state.colorsConfigStore);

  const combinedData = data.map((item: any, index: number) => {
    const data2Item = data2 && index < data2.length ? data2[index] : null;

    return {
      ...item,
      data2_avg_return: data2Item ? data2Item.avg_return : null,
      data2_upper_bound: data2Item ? data2Item.upper_bound : null,
      data2_lower_bound: data2Item ? data2Item.lower_bound : null,
    };
  });

  const allValues = [
    ...data.map((item: any) => item.avg_return),
    ...data.map((item: any) => item.upper_bound),
    ...data.map((item: any) => item.lower_bound),
    ...(data2 ? data2.map((item: any) => item.avg_return) : []),
    ...(data2 ? data2.map((item: any) => item.upper_bound) : []),
    ...(data2 ? data2.map((item: any) => item.lower_bound) : []),
  ].filter((value) => value !== null && value !== undefined);

  const yMin = Math.min(...allValues);
  const yMax = Math.max(...allValues);

  const adjustedMin = Math.floor(yMin / 10) * 10;
  const adjustedMax = Math.ceil(yMax / 10) * 10;

  const yTicks = Array.from(
    { length: (adjustedMax - adjustedMin) / 10 + 1 },
    (_, i) => adjustedMin + i * 10,
  );

  const xTicks = data.reduce((acc: string[], item: any, index: number) => {
    if (index % 12 === 0) {
      acc.push(item.date);
    }
    return acc;
  }, []);

  const customLegendRenderer = (props: any) => {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          marginTop: 28,
          gap: 38,
        }}
      >
        {/* Portfolio 1 */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <div
            style={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              backgroundColor: colorsConfig.portfolio1,
            }}
          />
          <span style={{ fontSize: 12 }}>
            {labels.portfolio1}
          </span>
        </div>

        {/* Portfolio 2 (only if data2 exists) */}
        {data2 && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <div
              style={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                backgroundColor: colorsConfig.portfolio2,
              }}
            />
            <span style={{ fontSize: 12 }}>
              {labels.portfolio2}
            </span>
          </div>
        )}
      </div>
    );
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div
          className={commonStyles.tooltipContainer}
          style={{
            backgroundColor: "white",
            border: `1px solid ${colorsConfig.FinecoGrayLight1}`,
            padding: "8px 10px",
            borderRadius: "8px",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          }}
        >
          <div style={{ fontSize: 12, fontWeight: "bold" }}>
            {utilsFormatDatePortfolioEvolutionChart(payload[0].payload.date)}
          </div>

          {payload.map((entry: any, index: number) => {
            if (entry.value === undefined) return null;
            return (
              <div key={`value-${index}`}>
                <p
                  style={{
                    color: entry.color,
                    margin: "3px 0",
                    fontSize: "12px",
                  }}
                >
                  {entry.name + ": " + formatItalianNumber(entry.value) + "%"}
                  {/* {parseMonths(entry.payload.month)} */}
                </p>
              </div>
            );
          })}
        </div>
      );
    }
    return null;
  };

  return (
    <div style={{ height: 384, padding: 0, marginTop: 20 }}>
      <ResponsiveContainer width="100%" height="100%" debounce={500}>
        <LineChart
          data={combinedData}
          margin={{ top: 5, right: 45, left: -24, bottom: 5 }}
        >
          <CartesianGrid
            // strokeDasharray="3 3"
            stroke={colorsConfig.FinecoGrayLight1}
            horizontal={true}
          />
          <XAxis
            dataKey="date"
            tickFormatter={utilsFormatDatePortfolioEvolutionChart}
            ticks={xTicks}
            tick={{ fill: colorsConfig.FinecoBlack, fontSize: 12 }}
            dx={20}
            tickMargin={10}
            tickLine={false}
          />
          <YAxis
            tickFormatter={(value) => `${value}%`}
            domain={[adjustedMin, adjustedMax]}
            ticks={yTicks}
            tick={{ fill: colorsConfig.FinecoBlack, fontSize: 12 }}
            tickLine={false}
          />
          {/* <Tooltip
            formatter={(value: number) => `${Number(value).toFixed(2)}%`}
            labelFormatter={utilsFormatDatePortfolioEvolutionChart}
            contentStyle={{ fontSize: 14 }}
          /> */}
          <Tooltip content={<CustomTooltip />} />
          <Legend content={customLegendRenderer} />

          <Line
            type="natural"
            dataKey="upper_bound"
            name="Scenario positivo (1)"
            stroke={colorsConfig.portfolio1}
            strokeDasharray="5 5"
            dot={false}
            strokeWidth={2}
            legendType="none"
            isAnimationActive={false}
          />
          <Line
            type="natural"
            dataKey="avg_return"
            name="Scenario medio (1)"
            stroke={colorsConfig.portfolio1}
            dot={false}
            strokeWidth={2}
            legendType="none"
            isAnimationActive={false}
          />
          <Line
            type="natural"
            dataKey="lower_bound"
            name="Scenario negativo (1)"
            stroke={colorsConfig.portfolio1}
            strokeDasharray="5 5"
            dot={false}
            strokeWidth={2}
            legendType="none"
            isAnimationActive={false}
          />

          {/* Second dataset lines with different property names */}
          {data2 && (
            <>
              <Line
                type="natural"
                dataKey="data2_upper_bound"
                name="Scenario positivo (2)"
                stroke={colorsConfig.portfolio2}
                strokeDasharray="5 5"
                dot={false}
                strokeWidth={2}
                legendType="none"
                isAnimationActive={false}
              />
              <Line
                type="natural"
                dataKey="data2_avg_return"
                name="Scenario medio (2)"
                stroke={colorsConfig.portfolio2}
                dot={false}
                strokeWidth={2}
                legendType="none"
                isAnimationActive={false}
              />
              <Line
                type="natural"
                dataKey="data2_lower_bound"
                name="Scenario negativo (2)"
                stroke={colorsConfig.portfolio2}
                strokeDasharray="5 5"
                dot={false}
                strokeWidth={2}
                legendType="none"
                isAnimationActive={false}
              />
            </>
          )}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default PortfolioEvolutionChart;
